import React, { useEffect, useReducer, useRef } from 'react';
import { Table } from 'rsuite';
import { getGeneratedDocuments } from '../../api/generateddocuments/generatedDocuments';
import { GeneratedDocument, GeneratedDocumentType } from '../../api/generateddocuments/generatedDocumentsApiTypes';
import { toDateTimeFormat } from '../../services/dateUtils';
import { utils } from '../../utils/utils';
import { PaginationScreenKey } from '../../services/paginationLimitService';
import usePaginationLimit from '../../hooks/paginationLimitHook';
import Pagination from '../Pagination';
import GeneratedDocumentStatusCell from './GeneratedDocumentStatusCell';
import GeneratedDocumentDownloadCell from './GeneratedDocumentDownloadCell';

const POLLING_INTERVAL_MS = 60_000;

const { Column, HeaderCell, Cell } = Table;

type Status = 'ready' | 'loading';

interface State {
  status: Status;
  documents: GeneratedDocument[];
  page: number;
}

const initialState: State = {
  status: 'ready',
  documents: [],
  page: 1,
};

type StatusSetAction = { type: 'status/set', payload: Status };
type DocumentsSetAction = { type: 'documents/set', payload: GeneratedDocument[] };
type PageSetAction = { type: 'page/set', payload: number };
type Action = StatusSetAction | DocumentsSetAction | PageSetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'status/set':
      return { ...state, status: action.payload };
    case 'documents/set':
      return { ...state, documents: action.payload };
    case 'page/set':
      return { ...state, page: action.payload };
    default:
      return state;
  }
}

interface Props {
  type: GeneratedDocumentType;
}

export default function GeneratedDocumentsTable({ type }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [limit, setLimit] = usePaginationLimit(PaginationScreenKey.GENERATED_DOCUMENTS);
  const abortControllerRef = useRef<AbortController>(new AbortController());

  async function fetchDocuments(): Promise<void> {
    dispatch({ type: 'status/set', payload: 'loading' });
    try {
      const documents = await getGeneratedDocuments(type, abortControllerRef.current.signal);
      dispatch({ type: 'documents/set', payload: documents });
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  useEffect(() => {
    fetchDocuments().then();

    const interval = setInterval(() => {
      fetchDocuments().then();
    }, POLLING_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      abortControllerRef.current.abort();
    };
  }, [type]);

  function handleChangePage(value: number): void {
    dispatch({ type: 'page/set', payload: value });
  }

  function handleChangeLimit(value: number): void {
    setLimit(value);
    handleChangePage(1);
  }

  const paginatedDocuments = utils.paginated(
    state.documents,
    { page: state.page, limit },
  );

  return (
    <>
      <Table
        data={paginatedDocuments}
        loading={state.status === 'loading'}
        autoHeight
      >
        <Column flexGrow={2}>
          <HeaderCell>TITLE</HeaderCell>
          <Cell dataKey="title" />
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>STATUS</HeaderCell>
          <Cell>
            {(rowData) => (
              <GeneratedDocumentStatusCell document={rowData as GeneratedDocument} />
            )}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>STARTED AT</HeaderCell>
          <Cell>
            {(rowData) => toDateTimeFormat((rowData as GeneratedDocument).createdAt)}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>FINISHED AT</HeaderCell>
          <Cell>
            {(rowData) => toDateTimeFormat((rowData as GeneratedDocument).completedAt)}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>DOWNLOAD</HeaderCell>
          <Cell>
            {(rowData) => (
              <GeneratedDocumentDownloadCell document={rowData as GeneratedDocument} />
            )}
          </Cell>
        </Column>
      </Table>
      <div className="mt-10">
        <Pagination
          total={state.documents.length}
          limit={limit}
          activePage={state.page}
          onChangePage={(value) => handleChangePage(value)}
          onChangeLimit={(value) => handleChangeLimit(value)}
        />
      </div>
    </>
  );
}
